/* Native scroll snapping remains usable without JavaScript. */
if (!customElements.get('bilona-process')) {
  customElements.define('bilona-process', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.cleanup = new AbortController();
      const { signal } = this.cleanup;
      this.track = this.querySelector('.bp__steps');
      this.items = [...this.querySelectorAll('.bp__step')];
      this.controls = this.querySelector('.bp__controls');
      this.media = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.clickAnimations = new Set();
      this.addEventListener('click', event => this.showClickFeedback(event), { signal });
      if (this.track && this.controls) {
        this.controls.hidden = false;
        this.previous = this.controls.querySelector('[data-direction="-1"]');
        this.next = this.controls.querySelector('[data-direction="1"]');
        this.status = this.controls.querySelector('.bp__status');
        this.controls.addEventListener('click', event => {
          const button = event.target.closest('button[data-direction]');
          if (!button || button.disabled) return;
          this.goTo(this.current + Number(button.dataset.direction));
        }, { signal });
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.update());
        }, { passive: true, signal });
        this.track.addEventListener('keydown', event => {
          if (!this.media.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const rtl = getComputedStyle(this.track).direction === 'rtl';
          const delta = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
          this.goTo(event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : this.current + delta);
        }, { signal });
        this.resize = new ResizeObserver(() => this.update());
        this.resize.observe(this.track);
        this.update();
        this.setupAutoplay(signal);
      }
      this.addEventListener('shopify:block:select', event => {
        const step = event.target.closest('.bp__step');
        if (step && this.media.matches) {
          this.editorSelected = true;
          this.scheduleAutoplay();
          this.goTo(this.items.indexOf(step), true);
        }
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.scheduleAutoplay();
      }, { signal });
      if (this.classList.contains('bp--animated') && !this.reducedMotion.matches) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('bp__revealed');
            this.observer.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        this.querySelectorAll('.bp__intro, .bp__gallery, .bp__benefits, .bp__process').forEach(element => this.observer.observe(element));
      }
    }
    showClickFeedback(event) {
      if (this.reducedMotion.matches || !this.classList.contains('bp--animated') || !this.classList.contains('bp--interactive')) return;
      const target = event.target.closest('.bp__button, .bp__arrow, a.bp__photo');
      if (!target || target.disabled || !this.contains(target)) return;
      const bounds = target.getBoundingClientRect();
      const wave = document.createElement('span');
      wave.className = 'bp__click-wave';
      wave.setAttribute('aria-hidden', 'true');
      wave.style.left = `${event.detail ? event.clientX - bounds.left : bounds.width / 2}px`;
      wave.style.top = `${event.detail ? event.clientY - bounds.top : bounds.height / 2}px`;
      target.append(wave);
      const size = Math.hypot(bounds.width, bounds.height) / 20;
      const animation = wave.animate([
        { transform: 'translate(-50%, -50%) scale(0)', opacity: .6 },
        { transform: `translate(-50%, -50%) scale(${size})`, opacity: 0 }
      ], { duration: 480, easing: 'ease-out' });
      this.clickAnimations.add(animation);
      const remove = () => { wave.remove(); this.clickAnimations.delete(animation); };
      animation.finished.then(remove, remove);
    }
    setupAutoplay(signal) {
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(3000, Number(this.dataset.autoplayInterval) || 4000);
      this.paused = false;
      this.pointerActive = false;
      this.keyboardActive = false;
      this.editorSelected = false;
      this.visible = false;
      this.playButton = this.querySelector('.bp__autoplay');
      const journey = this.querySelector('.bp__journey');
      this.playButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.playButton.setAttribute('aria-pressed', String(this.paused));
        this.playButton.setAttribute('aria-label', this.paused ? 'Play automatic process slides' : 'Pause automatic process slides');
        this.playButton.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
        this.scheduleAutoplay();
      }, { signal });
      journey.addEventListener('pointerdown', () => {
        this.pointerActive = true;
        this.scheduleAutoplay();
      }, { passive: true, signal });
      const release = () => {
        if (!this.pointerActive) return;
        this.pointerActive = false;
        this.scheduleAutoplay();
      };
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      journey.addEventListener('focusin', event => {
        this.keyboardActive = event.target.matches(':focus-visible');
        this.scheduleAutoplay();
      }, { signal });
      journey.addEventListener('focusout', event => {
        if (!journey.contains(event.relatedTarget)) {
          this.keyboardActive = false;
          this.scheduleAutoplay();
        }
      }, { signal });
      journey.addEventListener('keydown', () => {
        this.keyboardActive = true;
        this.scheduleAutoplay();
      }, { signal });
      this.track.addEventListener('scroll', () => this.scheduleAutoplay(), { passive: true, signal });
      this.controls.addEventListener('click', () => this.scheduleAutoplay(), { signal });
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), { signal });
      this.media.addEventListener('change', () => this.scheduleAutoplay(), { signal });
      this.reducedMotion.addEventListener('change', () => this.scheduleAutoplay(), { signal });
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.scheduleAutoplay();
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(journey);
      this.scheduleAutoplay();
    }
    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.classList.toggle('bp--process-visible', Boolean(this.visible && !document.hidden));
      const enabled = this.autoplayEnabled && this.media.matches && !this.reducedMotion.matches && this.classList.contains('bp--animated');
      if (this.playButton) this.playButton.hidden = !enabled;
      const running = enabled && this.visible && !document.hidden && !this.paused && !this.pointerActive && !this.keyboardActive && !this.editorSelected;
      // Automatic changes should not repeatedly interrupt screen readers.
      this.status?.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running || this.items.length < 2) return;
      this.autoplayTimer = setTimeout(() => {
        this.update();
        this.goTo((this.current + 1) % this.items.length);
        this.scheduleAutoplay();
      }, this.autoplayDelay);
    }
    update() {
      if (!this.track || !this.controls) return;
      const bounds = this.track.getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      this.current = this.items.reduce((best, item, index) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
      this.previous.disabled = this.current === 0;
      this.next.disabled = this.current === this.items.length - 1;
      this.status.textContent = `${this.current + 1} / ${this.items.length}`;
    }
    goTo(index, instant = false) {
      const item = this.items[Math.max(0, Math.min(index, this.items.length - 1))];
      if (!item) return;
      const rect = item.getBoundingClientRect();
      const bounds = this.track.getBoundingClientRect();
      this.track.scrollTo({ left: this.track.scrollLeft + rect.left + rect.width / 2 - bounds.left - bounds.width / 2,
        behavior: instant || this.reducedMotion.matches || !this.classList.contains('bp--animated') ? 'auto' : 'smooth' });
    }
    disconnectedCallback() {
      this.cleanup?.abort();
      this.clickAnimations?.forEach(animation => animation.cancel());
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.autoplayTimer);
      cancelAnimationFrame(this.frame);
    }
  });
}
